<template>
  <div class="shrink-0">
    <!-- HEADER: stays at top; main content scrolls separately -->
    <header
      class="relative z-40 flex items-center gap-3 border-b border-slate-200 bg-white px-3 py-2.5 sm:gap-4 sm:px-5"
    >
      <!-- ============== Left: brand + page hint ============== -->
      <div class="flex min-w-0 items-center gap-2.5">
        <span
          aria-hidden="true"
          class="hidden h-7 w-1 rounded-full bg-gradient-to-b from-orange-400 to-orange-600 sm:block"
        />
        <h2
          class="truncate font-bold tracking-tight text-slate-900"
          :title="companyDisplayName"
        >
          {{ companyDisplayName }}
        </h2>
      </div>

      <!-- ============== Right: actions + profile ============== -->
      <div class="ml-auto flex flex-shrink-0 items-center gap-1.5 sm:gap-2">
        <!-- Notifications -->
        <button
          type="button"
          class="relative inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:border-orange-200 hover:bg-orange-50 hover:text-orange-600"
          aria-label="Notifications"
          @click="toggleNotificationsModal"
        >
          <icon name="bell-notification" class="h-4 w-4" />
          <span
            v-if="store.notificationsCount > 0"
            class="absolute -right-1 -top-1 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-white bg-rose-500 px-1 text-[10px] font-bold leading-none text-white shadow-sm animate-pulse-badge"
          >
            {{ formatBadge(store.notificationsCount) }}
          </span>
        </button>

        <!-- Vertical divider -->
        <span
          aria-hidden="true"
          class="mx-0.5 hidden h-7 w-px bg-slate-200 sm:block"
        ></span>

        <!-- User chip -->
        <button
          type="button"
          class="group flex flex-shrink-0 items-center gap-2 rounded-xl border border-slate-200 bg-white py-1 pl-1 pr-1.5 transition hover:border-orange-200 hover:bg-orange-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/40 sm:py-1.5 sm:pl-1.5 sm:pr-2.5"
          :aria-expanded="showProfileMenu"
          aria-haspopup="true"
          aria-controls="profile-menu"
          aria-label="Account menu"
          @click="toggleProfileMenu"
        >
          <span class="relative shrink-0">
            <img
              :src="avatarSrc"
              alt=""
              class="h-7 w-7 rounded-lg object-cover ring-1 ring-slate-200 sm:h-8 sm:w-8"
            />
            <span
              class="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-emerald-400 shadow-sm"
              title="Online"
            />
          </span>
          <div class="hidden min-w-0 text-left sm:block">
            <p class="truncate text-[12.5px] font-semibold text-slate-900">
              {{ shortDisplayName }}
            </p>
            <p
              class="truncate text-[10px] font-medium uppercase tracking-wide text-slate-500"
            >
              {{ user?.role || "Member" }}
            </p>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="hidden h-3.5 w-3.5 text-slate-400 transition-transform duration-200 sm:block"
            :class="{ 'rotate-180 text-orange-500': showProfileMenu }"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      </div>
    </header>

    <!-- ============== Notifications modal ============== -->
    <Teleport to="body">
      <transition name="fade">
        <div
          v-if="showNotificationsModal"
          class="fixed inset-0 z-[120]"
          @click="closeNotificationsModal"
        >
          <div class="absolute inset-0 bg-transparent" aria-hidden="true" />
          <div class="absolute inset-0 flex justify-end px-4 py-16 pointer-events-none">
            <div class="pointer-events-auto" @click.stop>
              <NotificationPage @close="closeNotificationsModal" />
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- ============== Profile dropdown ============== -->
    <Teleport to="body">
      <transition name="fade">
        <div v-if="showProfileMenu" class="fixed inset-0 z-[100]">
          <div
            class="absolute inset-0 bg-transparent"
            aria-hidden="true"
            @click="closeProfileMenu"
          />
          <div
            id="profile-menu"
            role="menu"
            aria-label="Account menu"
            class="fixed right-2 top-[3.25rem] z-[101] flex max-h-[min(85vh,calc(100vh-4rem))] w-[min(20rem,calc(100vw-1rem))] flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-2xl shadow-slate-400/15 sm:right-4 sm:top-[3.75rem]"
            @click.stop
          >
            <!-- Header -->
            <div
              class="border-b border-slate-100 bg-gradient-to-br from-orange-50/90 via-white to-slate-50/40 px-4 py-3.5"
            >
              <div class="flex gap-3">
                <img
                  :src="avatarSrc"
                  alt=""
                  class="h-12 w-12 shrink-0 rounded-xl object-cover ring-2 ring-white shadow-sm"
                />
                <div class="min-w-0 flex-1">
                  <p class="truncate text-sm font-semibold text-slate-900">
                    {{ userDisplayName }}
                  </p>
                  <p
                    class="mt-0.5 truncate text-xs text-slate-500"
                    :title="user?.email"
                  >
                    {{ user?.email || "—" }}
                  </p>
                  <p
                    v-if="user?.employeeID"
                    class="mt-1 truncate font-mono text-[11px] text-slate-400"
                  >
                    ID {{ user.employeeID }}
                  </p>
                  <span
                    class="mt-2 inline-flex items-center rounded-full bg-orange-100/95 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-900"
                  >
                    {{ user?.role || "Role" }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <nav class="flex flex-col py-1.5" aria-label="Account links">
              <button
                v-for="setting in settingItems"
                :key="setting.label"
                type="button"
                role="menuitem"
                class="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-slate-700 transition hover:bg-orange-50/90 hover:text-orange-950"
                @click="goSetting(setting)"
              >
                <span
                  class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100/90 text-slate-600"
                >
                  <icon :name="setting.icon" class="h-4 w-4" />
                </span>
                <span class="font-medium">{{ setting.label }}</span>
              </button>
            </nav>

            <div class="border-t border-slate-100 px-2 pb-2 pt-1">
              <button
                type="button"
                role="menuitem"
                class="flex w-full items-center justify-center gap-2 rounded-xl border border-red-200/90 bg-red-50/80 px-3 py-2.5 text-sm font-medium text-red-800 transition hover:border-red-300 hover:bg-red-100/80"
                @click="logOut"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.8"
                  stroke="currentColor"
                  class="h-4 w-4 shrink-0"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15M18 12H9m0 0 3-3m-3 3 3 3"
                  />
                </svg>
                Log out
              </button>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
import { useFetchDataStore } from "@/store/pinia_store";
import NotificationPage from "./NotificationPage.vue";
import icon from "@/assets/icon.vue";
import axios from "axios";
import defaultBrandAvatar from "@/assets/va_logo.png";

export default {
  name: "TopBarPage",
  components: { icon, NotificationPage },
  data() {
    return {
      showNotificationsModal: false,
      showProfileMenu: false,
      user: null,
      companyLegalName: "",
      companyLogoDataUrl: "",
      settingItems: [
        {
          label: "Service provider profile",
          icon: "user",
          route: "/view-my-details",
        },
      ],
    };
  },
  computed: {
    store() {
      return useFetchDataStore();
    },
    companyDisplayName() {
      const name = (this.companyLegalName || "").trim();
      return name || "Company";
    },
    userDisplayName() {
      if (!this.user) return "User";
      const u = this.user;
      const parts = [u.first_name, u.middle_name, u.last_name].filter(Boolean);
      const joined = parts.join(" ").trim();
      return joined || u.email || "User";
    },
    /** Compact display name (first + last initial) for the chip. */
    shortDisplayName() {
      if (!this.user) return "User";
      const u = this.user;
      const first = (u.first_name || "").trim();
      const last = (u.last_name || "").trim();
      if (first && last) return `${first} ${last.charAt(0)}.`;
      if (first) return first;
      if (last) return last;
      return (u.email || "User").split("@")[0];
    },
    /** Employee photo from DB, else org logo, else static asset */
    avatarSrc() {
      const base = process.env.VUE_APP_API_BASE_URL || "";
      const fn =
        this.user?.profile_image && String(this.user.profile_image).trim();
      if (fn) {
        return `${base}/uploads/${fn}`;
      }
      const logo = (this.companyLogoDataUrl || "").trim();
      if (logo) {
        return logo;
      }
      return defaultBrandAvatar;
    },
  },

  methods: {
    /** Cap the notifications badge so a 3-digit count doesn't overflow the pill. */
    formatBadge(n) {
      const num = Number(n) || 0;
      return num > 99 ? "99+" : String(num);
    },
    toggleNotificationsModal() {
      this.showNotificationsModal = !this.showNotificationsModal;
      if (this.showNotificationsModal) {
        this.showProfileMenu = false;
      }
    },
    toggleProfileMenu() {
      this.showProfileMenu = !this.showProfileMenu;
      if (this.showProfileMenu) {
        this.showNotificationsModal = false;
      }
    },
    closeProfileMenu() {
      this.showProfileMenu = false;
    },
    closeNotificationsModal() {
      this.showNotificationsModal = false;
    },
    /** Refresh pending client count when admin returns to the tab (VAM may have submitted). */
    onDocumentVisibility() {
      if (document.visibilityState !== "visible") return;
      const rc = String(this.user?.role_code || "").toUpperCase();
      if (rc !== "ADMIN" && rc !== "HR") return;
      this.store.fetchClientDetails().then(() => {
        this.store.updateNotificationsCount(this.user);
      });
    },
    onDocumentKeydown(e) {
      if (e.key !== "Escape") return;
      if (this.showNotificationsModal) {
        this.closeNotificationsModal();
      }
      if (this.showProfileMenu) {
        this.closeProfileMenu();
      }
    },
    goSetting(setting) {
      if (setting.route && setting.route !== "/#") {
        this.$router.push(setting.route);
        this.closeProfileMenu();
      }
    },
    logOut() {
      this.closeProfileMenu();
      this.$router.push("/");
    },
    async fetchUser() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/auth/me`,
          { withCredentials: true },
        );
        if (response.data) {
          this.user = response.data;

          // Set current user in Pinia store
          this.store.currentUser = this.user;

          // Update notifications count with current user's context
          this.store.updateNotificationsCount(this.user);
        } else {
          this.$router.push("/");
          location.reload();
        }
      } catch (error) {
        console.error("Failed to fetch user:", error);
        this.$router.push("/");
      }
    },
    async fetchCompanyLegalName() {
      try {
        const { data } = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/hr-settings/organization-display`,
          { withCredentials: true },
        );
        if (data) {
          if (typeof data.company_name === "string") {
            this.companyLegalName = data.company_name;
          }
          if (data.logo_data_url != null && String(data.logo_data_url).trim()) {
            this.companyLogoDataUrl = String(data.logo_data_url).trim();
          }
        }
      } catch {
        this.companyLegalName = "";
        this.companyLogoDataUrl = "";
      }
    },
  },
  mounted() {
    document.addEventListener("keydown", this.onDocumentKeydown);
    this.fetchUser().then(async () => {
      const rc = String(this.user?.role_code || "").toUpperCase();
      const isHrStaff = rc === "ADMIN" || rc === "HR";
      const tasks = [
        this.store.fetchUserAccounts(),
        this.store.fetchSchedules(),
      ];
      if (isHrStaff) {
        tasks.push(this.store.fetchClientDetails());
      }
      await Promise.all(tasks);
      this.store.updateNotificationsCount(this.user);
    });
    this.fetchCompanyLegalName();
    document.addEventListener("visibilitychange", this.onDocumentVisibility);
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.onDocumentKeydown);
    document.removeEventListener("visibilitychange", this.onDocumentVisibility);
  },
};
</script>

<style scoped>
/* Fade animation for modal */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes pulseBadge {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(244, 63, 94, 0.55);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(244, 63, 94, 0);
  }
}

.animate-pulse-badge {
  animation: pulseBadge 1.6s infinite;
}
</style>
